import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import './PostMetrics.scss';

const PostMetrics = ({ readinessUpdate, tractionUpdate }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedReadinessType, setSelectedReadinessType] = useState('all');

  const getProgressColor = (level) => {
    if (level >= 4) return '#4CAF50';
    if (level >= 2.5) return '#FFC107';
    return '#F44336';
  };

  const handleReadinessClick = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedSection(expandedSection === type ? null : type);
  };

  const handleTypeChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedReadinessType(e.target.value);
  };

  const questions = {
    startup: [
      { key: 'clearProblemStatement', question: 'Do you have a clear and validated problem statement?' },
      { key: 'minimumViableProduct', question: 'Have you developed a Minimum Viable Product (MVP)?' },
      { key: 'definedTargetMarket', question: 'Do you have a defined target market and user persona?' },
      { key: 'marketResearchValidation', question: 'Have you conducted market research and validation?' },
      { key: 'clearRevenueModel', question: 'Do you have a clear revenue model?' }
    ],
    team: [
      { key: 'completeFoundingTeam', question: 'Do you have a complete founding team with complementary skills?' },
      { key: 'relevantIndustryExperience', question: 'Does your team have relevant industry experience?' },
      { key: 'definedRolesResponsibilities', question: 'Do you have defined roles and responsibilities?' },
      { key: 'advisorsAndMentors', question: 'Do you have advisors or mentors?' },
      { key: 'hiringAndScalingPlan', question: 'Do you have a hiring and scaling plan?' }
    ],
    business: [
      { key: 'detailedBusinessPlan', question: 'Do you have a detailed business plan?' },
      { key: 'keyMetricsForSuccess', question: 'Have you identified key metrics for success?' },
      { key: 'financialProjections', question: 'Do you have financial projections for the next 2-3 years?' },
      { key: 'keyPartnershipsResources', question: 'Have you identified key partnerships and resources?' },
      { key: 'clearCompetitiveAdvantage', question: 'Do you have a clear competitive advantage?' }
    ]
  };

  const renderReadinessSection = () => {
    if (!readinessUpdate) return null;

    // Check if there's any readiness data
    const hasReadinessData = ['startup', 'team', 'business'].some(type => 
      readinessUpdate[type] && Object.keys(readinessUpdate[type]).length > 0
    );

    if (!hasReadinessData) return null;

    const types = selectedReadinessType === 'all' 
      ? ['startup', 'team', 'business'].filter(type => readinessUpdate[type])
      : [selectedReadinessType];

    return (
      <div className="readiness-metrics">
        <div className="readiness-header">
          <h5>Readiness Assessment</h5>
          <select 
            value={selectedReadinessType} 
            onChange={handleTypeChange}
            onClick={(e) => e.stopPropagation()}
            className="type-selector"
          >
            <option value="all">All Readiness Levels</option>
            {Object.keys(readinessUpdate)
              .filter(type => type !== 'type' && readinessUpdate[type])
              .map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)} Readiness
                </option>
              ))
            }
          </select>
        </div>

        {types.map(type => {
          const data = readinessUpdate[type];
          if (!data || !data.readinessLevel) return null;

          return (
            <div key={type} className="readiness-type">
              <div 
                className="readiness-summary"
                onClick={(e) => handleReadinessClick(e, type)}
              >
                <div className="readiness-info">
                  <h6>{type.charAt(0).toUpperCase() + type.slice(1)} Readiness</h6>
                  <div className="progress-bar">
                    <div 
                      className="progress"
                      style={{
                        width: `${(data.readinessLevel / 5) * 100}%`,
                        backgroundColor: getProgressColor(data.readinessLevel)
                      }}
                    >
                      {data.readinessLevel}/5
                    </div>
                  </div>
                  <p className="description">{data.readinessDescription}</p>
                </div>
                {expandedSection === type ? <IoIosArrowUp /> : <IoIosArrowDown />}
              </div>

              {expandedSection === type && (
                <div 
                  className="readiness-details"
                  onClick={(e) => e.stopPropagation()}
                >
                  {questions[type].map(({ key, question }) => {
                    const answer = data[key];
                    if (!answer) return null;
                    
                    return (
                      <div key={key} className="detail-item">
                        <div className="question">{question}</div>
                        <div className="answer">{answer}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTractionSection = () => {
    if (!tractionUpdate?.update?.length) return null;

    // Check if there's any valid traction data
    const hasValidData = tractionUpdate.update.some(u => 
      (u.revenue !== undefined && u.revenue !== null) || 
      (u.users !== undefined && u.users !== null)
    );

    if (!hasValidData) return null;

    const chartData = {
      labels: tractionUpdate.update.map(u => {
        const date = new Date(u.date);
        return date.toLocaleString('default', { month: 'short', year: 'numeric' });
      }),
      datasets: [
        {
          label: 'Revenue',
          data: tractionUpdate.update.map(u => u.revenue),
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1
        },
        {
          label: 'Users',
          data: tractionUpdate.update.map(u => u.users),
          borderColor: 'rgb(255, 99, 132)',
          tension: 0.1
        }
      ]
    };

    return (
      <div 
        className="traction-metrics"
        onClick={(e) => e.stopPropagation()}
      >
        <h5>Traction Metrics</h5>
        <div className="chart-container">
          <Line data={chartData} />
          <div className="metrics-summary">
            <div className="metric">
              <h6>Total Users</h6>
              <p>{tractionUpdate.allUsers}</p>
            </div>
            <div className="metric">
              <h6>Latest Revenue</h6>
              <p>{tractionUpdate.update[tractionUpdate.update.length - 1]?.revenue}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Check if there's any valid data to show
  const hasReadinessData = readinessUpdate && ['startup', 'team', 'business'].some(type => 
    readinessUpdate[type] && Object.keys(readinessUpdate[type]).length > 0
  );
  
  const hasTractionData = tractionUpdate?.update?.length > 0;

  if (!hasReadinessData && !hasTractionData) return null;

  return (
    <div 
      className="post-metrics"
      onClick={(e) => e.stopPropagation()}
    >
      {renderReadinessSection()}
      {renderTractionSection()}
    </div>
  );
};

export default PostMetrics; 